// Guided recovery states for the collaborative Canvas (Mantis #1128, T-MCRO-024,
// MCRO001-D004 §4.1). Internal recovery codes are preserved untouched for
// telemetry; this module is the ONLY place they are translated into what an
// artist sees. Copy is owner-approved 2026-08-06 and must not drift: the exact
// strings here are asserted verbatim by tests, and every user-facing string is
// linted against BANNED_RECOVERY_TERMS.

export const RECOVERY_STATES = {
  RECONNECTING: 'reconnecting',
  UNSENDABLE: 'needs_choice_unsendable',
  REJECTED: 'needs_choice_rejected',
  SIGNED_OUT: 'signed_out',
  // T-MCRO-066 / MCRO001-F0066 (D004 §4.1.3): the page itself is not a secure
  // context, so the browser cannot verify the shared document. Readonly, one
  // honest copy, single reload action — never a conflict panel.
  SECURE_CONNECTION: 'secure_connection',
};

// Complete code → state map for every code enterRecovery can receive today:
// each literal code in useChoreographyCollaboration.js (call sites, the
// publishState failsafe, the pump rejection list and the socket
// rejected/authority-revoked handlers) appears here, and a test proves that
// completeness against the live source. Any code not listed — including new
// codes added later without a mapping decision — falls back to the safe
// generic needs_choice/edits-unsendable state, which never destroys text.
export const RECOVERY_CODE_TO_STATE = {
  // Connectivity: the service or transport is unreachable; edits stay queued.
  SERVICE_UNAVAILABLE: RECOVERY_STATES.RECONNECTING,
  // Identity/authority: the person must sign in again (one copy for all).
  AUTHORITY_LOST: RECOVERY_STATES.SIGNED_OUT,
  CHOREO_SUBJECT_TOKEN_UNAVAILABLE: RECOVERY_STATES.SIGNED_OUT,
  STEP_UP_REQUIRED: RECOVERY_STATES.SIGNED_OUT,
  // A single just-typed change was refused; refreshing usually fixes it.
  INVALID_UPDATE: RECOVERY_STATES.REJECTED,
  // The page is not a secure context (plain HTTP): SubtleCrypto is absent, so
  // source-hash verification cannot run at all (T-MCRO-066, D004 §4.1.3).
  SECURE_CONTEXT_REQUIRED: RECOVERY_STATES.SECURE_CONNECTION,
  // Queued local edits cannot be applied to the shared document automatically.
  ENTITY_CONFLICT: RECOVERY_STATES.UNSENDABLE,
  DRAFT_ROTATED: RECOVERY_STATES.UNSENDABLE,
  RESYNC_REQUIRED: RECOVERY_STATES.UNSENDABLE,
  SEQUENCE_GAP: RECOVERY_STATES.UNSENDABLE,
  INVALID_ENVELOPE: RECOVERY_STATES.UNSENDABLE,
  UNSUPPORTED_PROTOCOL: RECOVERY_STATES.UNSENDABLE,
  STALE_DRAFT: RECOVERY_STATES.UNSENDABLE,
  OFFLINE_QUEUE_FULL: RECOVERY_STATES.UNSENDABLE,
};

export const recoveryStateFor = (code) => RECOVERY_CODE_TO_STATE[code] || RECOVERY_STATES.UNSENDABLE;

// Reconnecting-tier failures auto-retry silently before any panel appears:
// three attempts with ~2s/4s/8s backoff, then escalation.
export const RECONNECT_BACKOFF_MS = [2000, 4000, 8000];

export const RECONNECTING_TOAST = 'Reconnecting…';
export const EXPORT_FAILED_MESSAGE = 'Couldn’t save a copy — try again';
export const MORE_OPTIONS_LABEL = 'More options';

// Owner-approved copy, 2026-08-06 — verbatim. Do not edit without a new
// owner-approved revision recorded in MCRO001-D004 §4.1.
export const RECOVERY_COPY = {
  [RECOVERY_STATES.RECONNECTING]: {
    title: 'Having trouble reconnecting',
    body: 'We’re still trying to reconnect you. Your edits are being saved as you type.',
    primary: 'Try again now',
    helper: '',
    options: ['Keep a copy of my edits and reload the page'],
  },
  [RECOVERY_STATES.UNSENDABLE]: {
    title: 'Some of your edits weren’t saved yet',
    body: 'We saved a copy of everything you typed, but a few recent changes can’t be added to the shared choreography automatically. This can happen when someone else made changes at the same time.',
    // MCRO001-F0050 / T-MCRO-050: owner-confirmed panel copy for the merge-first
    // primary. The pinned-copy test below asserts this exact wording and must be
    // updated together with the copy if it is ever revised.
    primary: 'Merge with the latest',
    helper: 'We’ll download a copy of your edits first, then merge them with the latest version everyone else sees. If anything can’t be merged automatically, we’ll ask you which version to keep.',
    // The safety net stays under More options: export/clipboard and the explicit
    // "Just show me the latest version" (discard) remain, so merge is the new
    // DEFAULT, never the only path, and text is never lost.
    options: ['Copy my edits to clipboard instead', 'Just show me the latest version'],
  },
  [RECOVERY_STATES.REJECTED]: {
    title: 'That change couldn’t be saved',
    body: 'The last thing you typed couldn’t be added to your choreography. We saved a copy of it. Refreshing usually fixes this.',
    primary: 'Save a copy and refresh',
    helper: '',
    options: ['Copy that change to clipboard instead', 'Discard that change and keep going'],
  },
  [RECOVERY_STATES.SIGNED_OUT]: {
    title: 'Please sign in again',
    body: 'For your safety, you’ll need to sign in again before you can keep working. Nothing you’ve typed has been lost.',
    primary: 'Sign in',
    helper: '',
    options: [],
  },
  // T-MCRO-066 / MCRO001-F0066 copy revision, owner-directed via WebUI issue #46,
  // recorded in MCRO001-D004 §4.1.1/§4.1.3 (2026-08-31). Verbatim; pinned by test.
  [RECOVERY_STATES.SECURE_CONNECTION]: {
    title: 'A secure connection is required',
    body: 'This page was opened over an unencrypted connection, so this browser can’t verify the shared choreography before editing. Reopen this page over HTTPS, then reload to continue.',
    primary: 'Reload the page',
    helper: '',
    options: [],
  },
};

// Engineer vocabulary that must never reach an artist. Enforced by test over
// every user-facing string in RECOVERY_COPY, the toast and the inline error.
export const BANNED_RECOVERY_TERMS = ['draft', 'admission', 'authorised', 'rebase', 'collaborative change', 'queue'];

// #17: within the UNSENDABLE tier, "a few recent changes can't be added...
// This can happen when someone else made changes at the same time" (the
// owner-approved 2026-08-06 copy above, left byte-for-byte unchanged) genuinely
// describes a CONCURRENT-EDIT conflict. It does NOT describe the other UNSENDABLE
// case — a persisted local queue from an earlier session that cannot be safely
// replayed onto the current document at all (compaction, rotation, or a
// server-side heal-in-place), where no other editor need be involved. Recovery
// entries for that family carry `staleQueue: true` (set at the exact call sites
// in useChoreographyCollaboration.js that already used this same "cannot be
// safely rebased" condition); CollaborativeRecovery.js swaps in this body ONLY
// for those, leaving title/primary/helper/options — and every other tier — the
// approved copy untouched. Additive only: does not alter the pinned object above.
export const RECOVERY_COPY_STALE_QUEUE_BODY = 'We saved a copy of everything you typed, but your earlier edits can’t be replayed automatically onto what’s here now — this isn’t caused by anyone else’s changes. Reconnecting keeps you in a live, up-to-date session either way.';

// #17 requirement 5(c): this browser's OWN local storage for the editor is
// failing (repeatedly, not a one-off self-healed hiccup — see
// collaborationQueue.js's `collaborationStorageIncident()`), which is a third,
// distinct condition from both the copy above and the owner-approved default:
// neither a concurrent editor nor an un-replayable old queue caused this. The
// export/copy safety net still ran before this panel opened — the copy says so
// — and storage has already been reset, so the session stays live either way.
export const RECOVERY_COPY_STORAGE_DEGRADED_BODY = 'This browser’s local storage for the editor is failing, so we can’t keep a durable copy of new edits here right now. We already saved a copy of what you had, and local storage has been reset.';
